import { Route, Routes, useParams } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { AxiosError } from 'axios';
import { GenericPageShimmer } from '../../../shimmer/GenericPageShimmer/GenericPageShimmer';
import { RetryApiAlert } from '../../../shared/components/RetryApiAlert/RetryApiAlert';
import { useQuery } from '../../../request/hooks/useQuery';
import { EntityUIState } from '../../../request/interfaces/entity-ui-state';
import { DestinationAPI } from '../../api/DestinationAPI';
import { DestinationConfigurationTab } from './DestinationConfigurationTab/DestinationConfigurationTab';
import { DESTINATION_OVERVIEW_DATA_ID_CONTRACT } from './destinationOverviewDataIdContract';
import { Destination } from '../../../shared/interfaces/destination';
import { DestinationFactory } from '../../DestinationList/factories/destinationFactory';
import { getDataIdsFromContract } from '../../../shared/utils/dataId';
import { getErrorMessageFromAxiosError } from '../../../request/utils';
import { DestinationHeader } from './DestinationHeader';
import { SecondarySidebar } from '../../../shared/components/SecondarySidebar/SecondarySidebar';
import { DestinationConnectedPipelines } from './DestinationConnectedPipelines/DestinationConnectedPipelines';

export function DestinationOverview() {
  const { id: destinationId } = useParams();

  const {
    data: destination,
    uiState,
    error: apiError,
    fetch: fetchDestination
  } = useQuery({
    dataSource$: () =>
      DestinationAPI.getDestination(Number(destinationId)).then(res => DestinationFactory(res.data))
  });

  useEffect(() => {
    if (destinationId) {
      fetchDestination();
    }
  }, [destinationId]);

  const dataIds = getDataIdsFromContract(DESTINATION_OVERVIEW_DATA_ID_CONTRACT);

  const tabs = useMemo(() => {
    if (!destination) {
      return [];
    }

    return [
      {
        label: 'Configuration',
        route: '/configuration',
        path: `configuration`,
        Component: DestinationConfigurationTab,
        icon: 'settings',
        shortcut: ''
      },
      {
        label: 'Connected Pipelines',
        route: '/connected-pipelines',
        path: `connected-pipelines`,
        Component: DestinationConnectedPipelines,
        icon: 'pipeline',
        shortcut: ''
      }
    ];
  }, [destination]);

  return (
    <div className='main-content-area' data-id={dataIds.container}>
      {uiState === EntityUIState.LOADING ? (
        <GenericPageShimmer dataId={dataIds.loadingShimmer} className='w-100' />
      ) : null}

      {uiState === EntityUIState.ERRORED ? (
        <RetryApiAlert
          title='Failed to load the Destination'
          subTitle={
            getErrorMessageFromAxiosError(apiError as AxiosError) || 'Retry loading the Destination'
          }
          secondaryCTAProps={{
            onClick: fetchDestination
          }}
          className='h-100 w-100'
          dataId={dataIds.retryApiAlert}
        />
      ) : null}

      {uiState === EntityUIState.IDLE ? (
        <div className='d-flex h-100'>
          <SecondarySidebar tabs={tabs} />

          <div className='main-content-wrapper flex-1'>
            <div className='main-content-area scrollable-content flex-1'>
              <div className='floating-header-container'>
                <DestinationHeader destination={destination as Destination} />
              </div>

              <div className='flex-1'>
                <Routes>
                  {tabs.map(tab => {
                    const TabComponent = tab.Component;
                    return (
                      <Route
                        key={tab.label}
                        path={tab.route}
                        element={<TabComponent destination={destination!} />}
                      />
                    );
                  })}
                </Routes>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
